import { apiService } from "@/services";
import { useMutation, useQuery } from "@tanstack/react-query";
import { handleApiErrorHidingServerText } from "@/utils";
import queryClient from "@/query-client";
import {
  ProhibitedKeyword,
  ProhibitedKeywordWithRemoval,
  BulkAddProhibitedKeywordsResponse,
  BulkDeleteProhibitedKeywordsResponse,
  PopulateCompetitorBrandsResponse,
} from "@/types/prohibited-keyword";
import { DEFAULT_STALE_TIME } from "@/constants";
import { getGlobalKeywordSet } from "@/resources/global-prohibited-keyword/global-prohibited-keyword.api";

type ProhibitedKeywordSnapshot = {
  queryKey: readonly unknown[];
  data: ProhibitedKeyword[] | undefined;
}[];

const getQueryKey = (brandId: number) =>
  ["prohibited-keywords", "list", brandId] as const;

const takeSnapshot = (brandId: number): ProhibitedKeywordSnapshot => {
  const queryKey = getQueryKey(brandId);
  const data = queryClient.getQueryData<ProhibitedKeyword[]>(queryKey);
  return [{ queryKey, data }];
};

const restoreSnapshot = (snapshot: ProhibitedKeywordSnapshot) => {
  for (const { queryKey, data } of snapshot) {
    queryClient.setQueryData(queryKey, data);
  }
};

let optimisticIdCounter = -1;

export const useGetProhibitedKeywords = (brandId: number | null) =>
  useQuery<ProhibitedKeyword[]>({
    queryKey: ["prohibited-keywords", "list", brandId],
    queryFn: () =>
      apiService.get<ProhibitedKeyword[]>("/prohibited-keywords", {
        brand_id: brandId,
      }),
    enabled: !!brandId,
    staleTime: DEFAULT_STALE_TIME,
  });

export const useAddProhibitedKeyword = () =>
  useMutation<
    ProhibitedKeywordWithRemoval,
    unknown,
    { brand_id: number; keyword: string; origin?: string },
    { snapshot: ProhibitedKeywordSnapshot }
  >({
    mutationFn: (data) =>
      apiService.post<ProhibitedKeywordWithRemoval>("/prohibited-keywords", data),
    onMutate: async (variables) => {
      const queryKey = getQueryKey(variables.brand_id);
      await queryClient.cancelQueries({ queryKey });
      const snapshot = takeSnapshot(variables.brand_id);

      const keyword = variables.keyword.trim().toLowerCase();

      // AUT-240: the Global list already blocks this keyword, so the server drops it.
      // Skip the optimistic row so it doesn't flash into the list and then vanish.
      if (getGlobalKeywordSet().has(keyword)) {
        return { snapshot };
      }

      const optimistic: ProhibitedKeyword = {
        id: optimisticIdCounter--,
        brand_id: variables.brand_id,
        organization_id: 0,
        keyword,
        origin: (variables.origin as ProhibitedKeyword["origin"]) ?? "manual",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      queryClient.setQueryData<ProhibitedKeyword[]>(queryKey, (prev) =>
        prev ? [...prev, optimistic] : [optimistic],
      );

      return { snapshot };
    },
    onError: (_error, variables, context) => {
      if (context) restoreSnapshot(context.snapshot);
      handleApiErrorHidingServerText(_error);
    },
    onSuccess: (serverData, variables) => {
      const queryKey = getQueryKey(variables.brand_id);
      queryClient.setQueryData<ProhibitedKeyword[]>(queryKey, (prev) => {
        if (!prev) return prev;
        return prev.map((k) =>
          k.id < 0 && k.keyword === variables.keyword.trim().toLowerCase()
            ? serverData
            : k,
        );
      });
    },
    onSettled: () => {
      queryClient.refetchQueries({ queryKey: ["search-term-sets"] });
    },
  });

export const useBulkAddProhibitedKeywords = () =>
  useMutation<
    BulkAddProhibitedKeywordsResponse,
    unknown,
    { brand_id: number; keywords: string[]; origin?: string },
    { snapshot: ProhibitedKeywordSnapshot; addedKeywords: string[] }
  >({
    mutationFn: (data) =>
      apiService.post<BulkAddProhibitedKeywordsResponse>(
        "/prohibited-keywords/bulk",
        data,
      ),
    onMutate: async (variables) => {
      const queryKey = getQueryKey(variables.brand_id);
      await queryClient.cancelQueries({ queryKey });
      const snapshot = takeSnapshot(variables.brand_id);

      const existing = new Set(
        (queryClient.getQueryData<ProhibitedKeyword[]>(queryKey) ?? []).map(
          (k) => k.keyword,
        ),
      );
      // AUT-240: keywords the Global list already blocks are dropped server-side,
      // so leave them out of the optimistic write instead of showing them then
      // yanking them back on refetch.
      const globalSet = getGlobalKeywordSet();

      const addedKeywords: string[] = [];
      const optimisticItems: ProhibitedKeyword[] = [];

      for (const raw of variables.keywords) {
        const kw = raw.trim().toLowerCase();
        if (kw === "" || existing.has(kw) || globalSet.has(kw)) continue;
        existing.add(kw);
        addedKeywords.push(kw);
        optimisticItems.push({
          id: optimisticIdCounter--,
          brand_id: variables.brand_id,
          organization_id: 0,
          keyword: kw,
          origin: (variables.origin as ProhibitedKeyword["origin"]) ?? "manual",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      }

      queryClient.setQueryData<ProhibitedKeyword[]>(queryKey, (prev) =>
        prev ? [...prev, ...optimisticItems] : optimisticItems,
      );

      return { snapshot, addedKeywords };
    },
    onError: (_error, _variables, context) => {
      if (context) restoreSnapshot(context.snapshot);
      handleApiErrorHidingServerText(_error);
    },
    onSuccess: (_serverData, variables) => {
      queryClient.invalidateQueries({
        queryKey: getQueryKey(variables.brand_id),
      });
    },
    onSettled: () => {
      queryClient.refetchQueries({ queryKey: ["search-term-sets"] });
    },
  });

export const useDeleteProhibitedKeyword = () =>
  useMutation<
    unknown,
    unknown,
    { id: number; brandId: number },
    { snapshot: ProhibitedKeywordSnapshot }
  >({
    mutationFn: ({ id }) => apiService.delete(`/prohibited-keywords/${id}`),
    onMutate: async (variables) => {
      const queryKey = getQueryKey(variables.brandId);
      await queryClient.cancelQueries({ queryKey });
      const snapshot = takeSnapshot(variables.brandId);

      queryClient.setQueryData<ProhibitedKeyword[]>(queryKey, (prev) =>
        prev ? prev.filter((k) => k.id !== variables.id) : prev,
      );

      return { snapshot };
    },
    onError: (_error, _variables, context) => {
      if (context) restoreSnapshot(context.snapshot);
      handleApiErrorHidingServerText(_error);
    },
    onSettled: (_data, _error, variables) => {
      // AUT-220: reconcile the list with the server after the delete settles. Without
      // this the optimistic write was the only source of truth (staleTime is 5 min),
      // so a rolled-back failure could resurrect a row a later delete had removed.
      queryClient.invalidateQueries({ queryKey: getQueryKey(variables.brandId) });
      queryClient.refetchQueries({ queryKey: ["search-term-sets"] });
    },
  });

export const usePopulateCompetitorBrands = () =>
  useMutation<
    PopulateCompetitorBrandsResponse,
    unknown,
    { brand_id: number }
  >({
    mutationFn: (data) =>
      apiService.post<PopulateCompetitorBrandsResponse>(
        "/prohibited-keywords/populate-competitors",
        data,
      ),
    onSuccess: (_serverData, variables) => {
      queryClient.invalidateQueries({
        queryKey: getQueryKey(variables.brand_id),
      });
    },
    onSettled: () => {
      queryClient.refetchQueries({ queryKey: ["search-term-sets"] });
    },
    onError: (error) => handleApiErrorHidingServerText(error),
  });

// Bulk remove. No snapshot: a bulk request the SPA sees fail may have half-succeeded
// on the server, so the failure path re-reads the list instead of restoring it.
export const useBulkDeleteProhibitedKeywords = () =>
  useMutation<
    BulkDeleteProhibitedKeywordsResponse,
    unknown,
    { brandId: number; ids: number[] }
  >({
    mutationFn: ({ brandId, ids }) =>
      apiService.post<BulkDeleteProhibitedKeywordsResponse, { brand_id: number; ids: number[] }>(
        "/prohibited-keywords/bulk-delete",
        { brand_id: brandId, ids },
      ),
    onMutate: async ({ brandId, ids }) => {
      const queryKey = getQueryKey(brandId);
      await queryClient.cancelQueries({ queryKey });
      const removed = new Set(ids);
      queryClient.setQueryData<ProhibitedKeyword[]>(queryKey, (prev) =>
        prev ? prev.filter((k) => !removed.has(k.id)) : prev,
      );
    },
    onError: (error) => handleApiErrorHidingServerText(error),
    onSettled: (_data, _error, { brandId }) => {
      queryClient.invalidateQueries({ queryKey: getQueryKey(brandId) });
      queryClient.refetchQueries({ queryKey: ["search-term-sets"] });
    },
  });
